import { ListPlus } from 'lucide-react';
import { useMemo, useState } from 'react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import {
  useContentTypeId,
  useCustomFieldDefs,
  useCustomFieldValues,
  useUpsertCustomFieldValue,
} from '../queries';
import type {
  CustomFieldDef,
  CustomFieldRawValue,
  CustomFieldValue,
} from '../types';

interface CustomFieldsPanelProps {
  /** Django-style "app_label.model" — e.g. "crm.deal". */
  entity: string;
  /** Target's PK. */
  entityId: string | number;
  /** Optional title shown on the card header. */
  title?: string;
}

/**
 * Wave 14 — renders org-defined custom fields for one entity record.
 *
 * Fetches:
 *   * defs for the entity (org-scoped)
 *   * values currently set on this target
 *   * the ContentType id for the entity (cached forever once resolved)
 *
 * On change → debounce-free POST-as-upsert. Save state lives entirely in
 * `useUpsertCustomFieldValue`'s mutation; the inputs render the value from
 * either local edits (in-flight) or the server-read row.
 */
export function CustomFieldsPanel({ entity, entityId, title }: CustomFieldsPanelProps) {
  const defs = useCustomFieldDefs(entity);
  const values = useCustomFieldValues(entity, entityId);
  const ct = useContentTypeId(entity);
  const upsert = useUpsertCustomFieldValue(entity, entityId);

  // Local edit buffer keyed by definition id, so a user can type freely without
  // every keystroke firing a PATCH. We commit on blur (text/number/date) or
  // immediately for select/checkbox (one-tap inputs). After a successful save,
  // the per-def entry is dropped so we re-read the server value (see `commit`).
  const [edits, setEdits] = useState<Record<number, CustomFieldRawValue>>({});

  const valueByDef = useMemo(() => {
    const m = new Map<number, CustomFieldValue>();
    (values.data ?? []).forEach((v) => m.set(v.definition, v));
    return m;
  }, [values.data]);

  function clearEdit(defId: number) {
    setEdits((prev) => {
      if (!(defId in prev)) return prev;
      const next = { ...prev };
      delete next[defId];
      return next;
    });
  }

  function commit(def: CustomFieldDef, raw: CustomFieldRawValue) {
    if (!ct.data) return;
    upsert.mutate(
      {
        definition: def.id,
        content_type: ct.data.id,
        object_id: String(entityId),
        value: raw,
      },
      { onSuccess: () => clearEdit(def.id) },
    );
  }

  function currentValue(def: CustomFieldDef): CustomFieldRawValue {
    if (def.id in edits) return edits[def.id]!;
    return valueByDef.get(def.id)?.value ?? null;
  }

  const defList = defs.data ?? [];

  // Hide the whole section unless the org has defined custom fields for this
  // entity — no empty placeholder. (Defs are managed in Settings → Custom
  // Fields.) Gate on defs only; values/content-type may still be resolving.
  if (defs.isPending || defList.length === 0) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <ListPlus className="size-4" /> {title ?? 'Custom fields'}
        </CardTitle>
        <CardDescription>
          Org-defined attributes for this {entity.split('.').pop()}. Saved automatically.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {defList.map((def) => (
          <FieldRow
            key={def.id}
            def={def}
            value={currentValue(def)}
            onLocalChange={(v) => setEdits((prev) => ({ ...prev, [def.id]: v }))}
            onCommit={(v) => commit(def, v)}
          />
        ))}
      </CardContent>
    </Card>
  );
}

interface FieldRowProps {
  def: CustomFieldDef;
  value: CustomFieldRawValue;
  onLocalChange: (next: CustomFieldRawValue) => void;
  onCommit: (next: CustomFieldRawValue) => void;
}

function FieldRow({ def, value, onLocalChange, onCommit }: FieldRowProps) {
  const id = `cf-${def.id}`;
  const labelEl = (
    <label htmlFor={id} className="text-sm font-medium">
      {def.label}
      {def.required ? <span className="text-destructive ml-1">*</span> : null}
    </label>
  );

  switch (def.field_type) {
    case 'text':
      return (
        <div className="space-y-1">
          {labelEl}
          <Input
            id={id}
            value={typeof value === 'string' ? value : ''}
            onChange={(e) => onLocalChange(e.target.value)}
            onBlur={(e) => onCommit(e.target.value)}
          />
        </div>
      );
    case 'number':
      return (
        <div className="space-y-1">
          {labelEl}
          <Input
            id={id}
            type="number"
            value={typeof value === 'number' ? value : (value ?? '') as string}
            onChange={(e) =>
              onLocalChange(e.target.value === '' ? null : Number(e.target.value))
            }
            onBlur={(e) =>
              onCommit(e.target.value === '' ? null : Number(e.target.value))
            }
          />
        </div>
      );
    case 'date':
      return (
        <div className="space-y-1">
          {labelEl}
          <Input
            id={id}
            type="date"
            value={typeof value === 'string' ? value : ''}
            onChange={(e) => onLocalChange(e.target.value || null)}
            onBlur={(e) => onCommit(e.target.value || null)}
          />
        </div>
      );
    case 'select':
      return (
        <div className="space-y-1">
          {labelEl}
          <Select
            value={typeof value === 'string' ? value : ''}
            onValueChange={(v) => {
              onLocalChange(v);
              onCommit(v);
            }}
          >
            <SelectTrigger id={id}>
              <SelectValue placeholder="Choose…" />
            </SelectTrigger>
            <SelectContent>
              {def.options.map((opt) => (
                <SelectItem key={opt} value={opt}>{opt}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      );
    case 'checkbox':
      return (
        <div className="flex items-center justify-between gap-3">
          {labelEl}
          <Switch
            id={id}
            checked={Boolean(value)}
            onCheckedChange={(next) => {
              onLocalChange(next);
              onCommit(next);
            }}
            aria-label={def.label}
          />
        </div>
      );
    default:
      return null;
  }
}
